import * as React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useIntl } from 'react-intl';

import imagePlaceholder from 'src/assets/images/placeholder.webp';
import { EVENT_TYPE } from 'src/features/eventLog/types';
import { useListGatewayPasses } from 'src/features/gatewayPass/api/useListGatewayPasses';
import { useGetPastStay } from 'src/features/stay/api/useGetPastStay';
import { useListPastStayPeriods } from 'src/features/stay/api/useListPastStayPeriods';
import ActiveStayPeriods from 'src/features/stay/components/sidePanel/ActiveStayPeriods';
import ActiveStaySidePanelButtons from 'src/features/stay/components/sidePanel/ActiveStaySidePanelButtons';
import OverviewActiveStays from 'src/features/stay/components/sidePanel/OverviewActiveStays';
import SidePanelTabs from 'src/features/stay/components/sidePanel/SidePanelTabs';
import { STAY_TYPE } from 'src/features/stay/types';
import ImageCarousel from 'src/features/visitor/components/carousel/ImageCarousel';
import { visitorMessages } from 'src/features/visitor/utils/messages';
import { formatTimestampToCustomId } from 'src/features/visitor/utils/timestampToId';
import { useParking } from 'src/providers/ParkingProvider';
import useSorting from 'src/registry/hooks/useSorting';
import { Badge } from 'src/registry/ui/Badge';
import { Button } from 'src/registry/ui/Button';
import {
    DialogBody,
    DialogClose,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
} from 'src/registry/ui/Dialog';
import { Skeleton } from 'src/registry/ui/Skeleton';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';

type PastStaysSidePanelProps = {
    selectedStayId?: string;
    eventType?: EVENT_TYPE;
};

const PastStaysSidePanel = ({ selectedStayId, eventType }: PastStaysSidePanelProps) => {
    const { formatMessage, locale } = useIntl();

    const { parkingId } = useParking();

    const { querySortingOptions } = useSorting({
        initialSortingState: [{ id: 'start', desc: true }],
    });

    const { data: selectedPastStay } = useGetPastStay({
        parkingId,
        stayId: selectedStayId ?? '',
        config: {
            enabled: !!selectedStayId,
        },
    });

    const { data: pastStayPeriodsData } = useListPastStayPeriods({
        parkingId,
        stayId: selectedStayId ?? '',
        config: { enabled: !!selectedStayId },
        ...querySortingOptions,
    });

    const { data: filteredGatewayPassesByStayId, isLoading } = useListGatewayPasses({
        parkingId,
        past_stay: selectedStayId ?? '',
        limit: 500,
        ordering: 'time',
        config: { enabled: !!selectedStayId },
    });

    const gatewayPassesPhotos = React.useMemo(() => {
        return filteredGatewayPassesByStayId?.results.map((pass) => {
            return {
                image: pass.photo ?? imagePlaceholder,
                title: pass.gateway.translations.find((z) => z.language === locale)?.name,
                subtitle: `${formatMessage(visitorMessages.pass)} ${formatTimestampToCustomId(pass.time)}`,
            };
        });
    }, [filteredGatewayPassesByStayId?.results, formatMessage, locale]);

    const pastPeriodsResults = pastStayPeriodsData?.results ?? [];

    return (
        <>
            {selectedPastStay ? (
                <DialogHeader>
                    <div className="flex items-center gap-2">
                        <DialogTitle className="pr-0 font-mono">
                            {formatMessage(visitorMessages.parkingStay)}
                        </DialogTitle>
                        <Badge theme="red">
                            <FontAwesomeIcon icon={ICON_MAP.WAVE_PULSE} />
                            {formatMessage(visitorMessages.pastStayFull)}
                        </Badge>
                    </div>
                    <DialogDescription>{`#${selectedPastStay.ref}`}</DialogDescription>
                </DialogHeader>
            ) : (
                <DialogHeader>
                    <Skeleton className="h-7 w-32" />
                    <Skeleton className="h-5 w-60" />
                </DialogHeader>
            )}

            <DialogSeparator />
            <DialogBody>
                <ImageCarousel imageSlides={gatewayPassesPhotos} isLoading={isLoading} />
                <ActiveStaySidePanelButtons stayId={selectedStayId} stayType={STAY_TYPE.PAST} eventType={eventType} />
                <SidePanelTabs
                    tabs={[
                        {
                            key: 'overview',
                            label: formatMessage(visitorMessages.overview),
                            content: <OverviewActiveStays selectedStay={selectedPastStay} type={STAY_TYPE.PAST} />,
                        },
                        {
                            key: 'periods',
                            label: formatMessage(visitorMessages.periods),
                            content: <ActiveStayPeriods stayPeriodsData={pastPeriodsResults} />,
                        },
                    ]}
                    defaultActiveKey="overview"
                />
            </DialogBody>
            <DialogFooter>
                <DialogClose asChild>
                    <Button variant="outline" className="flex-1">
                        {formatMessage(generalMessages.close)}
                    </Button>
                </DialogClose>
            </DialogFooter>
        </>
    );
};

export default PastStaysSidePanel;
